'use client';

import useDelayedLoader from '@/hooks/use-delayed-loader';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { StoreApi } from 'zustand';

import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import { usePathname } from 'next/navigation';

import ApiError from '@/components/api-error';
import AppLoader from '@/components/ui/app-loader';

import { CreateNewWorkspaceFullScreen } from '@/features/user-settings/components/common/create-new-workspace';
import { APPLICANT_MODE_WORKSPACE_ID } from '@/features/user-settings/types/workspace-types';

import WorkspaceChooseScreen from '@/shared/components/workspace-choose-screen';
import {
	APP_ROUTES,
	ONBOARDING_GUARD_EXEMPT_ROUTES,
	REFERRER_ALLOWED_ROUTES,
	matchAppRoute,
} from '@/shared/constants/navigation';
import { QUERY_PARAMS } from '@/shared/constants/query-params';
import DialogProvider from '@/shared/contexts/dialog/providers/dialog-provider';
import useIntercomWidget from '@/shared/hooks/use-intercom-widget';
import useSelectWorkspace from '@/shared/hooks/useSelectWorkspace';
import { getUserDetails } from '@/shared/services/user';
import { updateDefaultWorkspace } from '@/shared/services/user/user';
import { setSentryUser } from '@/shared/utils/sentry-utils';
import { getUseStoreHookSafe } from '@/shared/utils/store';
import { tabStorage } from '@/shared/utils/tab-storage';

import { useRouter } from '@shared/hooks/use-router';

// import { matchesRoute } from '@/utils/common';

import {
	handleUserDetailsSuccess,
	handleWorkspaceUpdateSuccess,
	isOnboardingComplete,
} from '../app-wrapper-helper';
import createAppWrapperStore, { AppWrapperStoreState } from '../stores/app-wrapper-store';

interface ContextValue {
	store: StoreApi<AppWrapperStoreState>;
	refetchUserDetails: () => Promise<void>;
	updateDefaultWorkspaceId: (workspaceId: string, localOnly?: boolean) => void;
	handleShowWorkspaceSelector: ({
		showCreateWorkspace,
		showSelectWorkspace,
		callback,
	}: {
		showCreateWorkspace?: boolean;
		showSelectWorkspace?: boolean;
		callback: () => void;
		source?: string; // source from where this component triggered
	}) => void;
	handleWorkspaceSelectionComplete: () => void;
}

const AppContext = createContext<ContextValue>({} as ContextValue);

export default function AppProvider({
	children,
	isAnonymous = false,
}: {
	children: React.ReactNode;
	isAnonymous?: boolean;
}) {
	const router = useRouter();
	const pathname = usePathname();
	const queryClient = useQueryClient();

	// Create store with initial state
	const store = useMemo(
		() =>
			createAppWrapperStore({
				isAnonymousMode: isAnonymous,
			}),
		[]
	);

	// Mutation for fetching user details
	const fetchUserDetailsMutation = useMutation({
		mutationFn: getUserDetails,
		onMutate: () => {
			// Only show the full-screen loader on the initial load, not on silent background refetches.
			// isRefetching is set to true before mutateAsync() is called in handleRefetchUserDetails,
			// so we can use it to distinguish between the two cases.
			if (!store.getState().isRefetching) {
				store.getState().setIsAppLoading(true);
			}
		},
		onSuccess: (userDetailsData) => {
			handleUserDetailsSuccess(userDetailsData, store);
			setSentryUser({
				id: userDetailsData.email,
				email: userDetailsData.email,
				name: userDetailsData.firstName + ' ' + userDetailsData.lastName,
			});
		},
		onError: () => {
			store.getState().setIsAppLoading(false);
			store.getState().setRefetching(false);
		},
	});

	// Mutation for updating default workspace
	const updateDefaultWorkspaceMutation = useMutation({
		mutationFn: updateDefaultWorkspace,
		onSuccess: (_, variables) => {
			handleWorkspaceUpdateSuccess(variables, queryClient, store);
		},
	});

	// Memoized callbacks
	const handleRefetchUserDetails = useCallback(async () => {
		store.getState().setRefetching(true);
		await fetchUserDetailsMutation.mutateAsync();
	}, [store]);

	const handleUpdateDefaultWorkspaceId = useCallback(
		async (workspaceId: string, localOnly = false) => {
			if (!localOnly) {
				await updateDefaultWorkspaceMutation.mutateAsync({
					page_id: workspaceId,
				});
			}

			// Update tab storage
			const { tabStorage } = await import('@/shared/utils/tab-storage');
			tabStorage.set('selectedWorkspaceId', workspaceId);
			tabStorage.set('isFreshLogin', false);
		},
		[]
	);

	const handleShowWorkspaceSelector = useCallback(
		({
			showCreateWorkspace = false,
			showSelectWorkspace = false,
			callback,
			source,
		}: {
			showCreateWorkspace?: boolean;
			showSelectWorkspace?: boolean;
			callback: () => void;
			source?: string;
		}) => {
			store.getState().setWorkspaceSelector({
				showCreateWorkspace,
				showSelector: showSelectWorkspace,
				callback,
				source,
			});
			router.push(
				`${APP_ROUTES.WORKSPACE_OPS}?${QUERY_PARAMS.REDIRECT_PATH}=${window.location.pathname + window.location.search}`
			);
		},
		[router, store]
	);

	const handleWorkspaceSelectionComplete = useCallback(() => {
		const workspaceSelector = store.getState().workspaceSelector;
		workspaceSelector?.callback?.();
		store.getState().resetWorkspaceSelector();
	}, [store]);

	const handlePathnameChangeCallback = useCallback(() => {
		if (pathname === APP_ROUTES.WORKSPACE_OPS) return;

		store.getState().resetWorkspaceSelector();

		const pageAccessDenied = store.getState().pageAccessDenied;
		if (pageAccessDenied?.workspace || pageAccessDenied?.feature) {
			store.getState().setPageAccessDenied({
				workspace: false,
				feature: false,
				message: '',
			});
		}
	}, [pathname, store]);

	const handleMixpanelSessionReplayCallback = useCallback(() => {
		// Session recording is disabled via record_sessions_percent: 0 in mixpanel config
		// TODO: enable session replays next month (nov) with reduced sample rate of 20%-40%
		// When re-enabling, uncomment the logic below and remove record_sessions_percent: 0 from mixpanel config
		// const isMixpanelSessionReplayBlocked =
		// 	MIXPANEL_SESSION_REPLAYS_BLOCKED_ROUTES.some((route) => matchesRoute(route, pathname)) ||
		// 	APP_ENVS.MIXPANEL_ENV !== 'production';
		// if (isMixpanelSessionReplayBlocked) {
		// 	try {
		// 		mixpanel.stop_session_recording();
		// 	} catch (e) {
		// 		console.error('Error stopping mixpanel session recording', e);
		// 	}
		// } else {
		// 	try {
		// 		mixpanel.start_session_recording();
		// 	} catch (e) {
		// 		console.error('Error starting mixpanel session recording', e);
		// 	}
		// }
	}, [pathname]);

	// Effects
	useEffect(() => {
		// Initial fetch - skip if in anonymous mode
		const isAnonymousMode = store.getState().isAnonymousMode;
		if (!isAnonymousMode) {
			fetchUserDetailsMutation.mutate();
		} else {
			// Set loading to false immediately for anonymous mode
			store.getState().setIsAppLoading(false);
		}
	}, []);

	useEffect(() => {
		handleMixpanelSessionReplayCallback();
		handlePathnameChangeCallback();
	}, [pathname]);

	return (
		<AppContext.Provider
			value={{
				store,
				refetchUserDetails: handleRefetchUserDetails,
				updateDefaultWorkspaceId: handleUpdateDefaultWorkspaceId,
				handleShowWorkspaceSelector: handleShowWorkspaceSelector,
				handleWorkspaceSelectionComplete: handleWorkspaceSelectionComplete,
			}}
		>
			<DialogProvider>
				<AppContent
					isError={fetchUserDetailsMutation?.isError}
					store={store}
				>
					{children}
				</AppContent>
			</DialogProvider>
		</AppContext.Provider>
	);
}

// Separate component for the conditional rendering logic
function AppContent({
	children,
	isError,
	store,
}: {
	children: React.ReactNode;
	isError?: boolean;
	store: StoreApi<AppWrapperStoreState>;
}) {
	const router = useRouter();
	const pathname = usePathname();
	const isRefetchingUserDetails = useAppStore((state) => state.isRefetching);
	const workspaces = useAppStore((state) => state.workspaces);
	const isAppLoading = useAppStore((state) => state.isAppLoading);
	const showLoader = useDelayedLoader(isAppLoading);
	const setSelectedWorkspaceId = useAppStore((state) => state.setSelectedWorkspaceId);
	const userEmail = useAppStore((state) => state.userDetails?.email);
	const workspaceSelector = useAppStore((state) => state.workspaceSelector);
	const userDetails = useAppStore((state) => state.userDetails);
	const isAnonymousMode = useAppStore((state) => state.isAnonymousMode);
	const currentUserRole = useAppStore((state) => state.selectedWorkspace?.currentUserRole);
	const referralsEnabled = useAppStore((state) => state.selectedWorkspace?.referralsEnabled);
	const selectedWorkspaceHandle = useAppStore((state) => state.selectedWorkspace?.handle);

	const { hideIntercomLauncher } = useIntercomWidget();

	const { workspaceId: selectedWorkspaceId } = useSelectWorkspace({
		allWorkspaces: workspaces,
		refetching: isRefetchingUserDetails,
	});

	useEffect(() => {
		if (selectedWorkspaceId) setSelectedWorkspaceId(selectedWorkspaceId);
	}, [selectedWorkspaceId]);

	// In-app onboarding guard: any logged-in user who hasn't finished onboarding
	// (no name / no workspace) is routed to the `/ai` agent, wherever they
	// land. This replaces the old full-screen onboarding overlay — same canonical
	// predicate, redirect instead of render. Exempt: /ai itself, plus tokenized
	// deep-link surfaces whose URL a bounce would destroy.
	useEffect(() => {
		if (isAnonymousMode || !userDetails) return;
		if (isOnboardingComplete(userDetails)) return;
		if (ONBOARDING_GUARD_EXEMPT_ROUTES.some((route) => matchAppRoute(route, pathname ?? '')))
			return;
		router.replace(APP_ROUTES.AI_AGENT);
	}, [isAnonymousMode, userDetails, pathname, router]);

	// Mirror the workspace handle into tab storage so analytics can attach it per tab (see
	// emitMixpanelEvent). Reactive, so it stays in sync on switch and settings edits.
	useEffect(() => {
		if (selectedWorkspaceHandle) tabStorage.set('workspaceHandle', selectedWorkspaceHandle);
		else tabStorage.remove('workspaceHandle');
	}, [selectedWorkspaceHandle]);

	useEffect(() => {
		if (selectedWorkspaceId && currentUserRole === 'REFERRER' && referralsEnabled) {
			const isOnAllowedRoute = REFERRER_ALLOWED_ROUTES.some((route) =>
				matchAppRoute(route, pathname ?? '')
			);

			if (!isOnAllowedRoute) {
				router.replace(APP_ROUTES.REFERRALS);
				setTimeout(() => {
					router.replace(APP_ROUTES.REFERRALS);
				}, 1000);
			}
		}
	}, [selectedWorkspaceId, currentUserRole, referralsEnabled]);

	useEffect(() => {
		const selectedWorkspaceId = store.getState().selectedWorkspaceId;
		if (selectedWorkspaceId === APPLICANT_MODE_WORKSPACE_ID) {
			router.push(APP_ROUTES.MESSAGES);
		}
	}, [isAppLoading]);

	// Hide Intercom launcher for all logged-in routes to avoid UI conflicts
	useEffect(() => {
		if (isAnonymousMode || !userEmail) return;
		hideIntercomLauncher();
	}, [pathname, userEmail]);

	// If in anonymous mode, skip all loading/error/onboarding screens
	if (isAnonymousMode) {
		return children;
	}

	if (isAppLoading) {
		return (
			<main className="flex h-screen w-screen items-center justify-center overflow-hidden">
				{showLoader && <AppLoader />}
			</main>
		);
	}

	// Error state
	if (isError || !userEmail) {
		return (
			<main className="h-screen w-screen overflow-hidden">
				<ApiError />
			</main>
		);
	}
	if (workspaceSelector.showSelector) return <WorkspaceChooseScreen />;

	if (workspaceSelector.showCreateWorkspace)
		return <CreateNewWorkspaceFullScreen source={workspaceSelector.source} />;

	return children;
}

type Selector<TState, TSelected> = (state: TState) => TSelected;

export function useAppCallbacks<TSelected>(
	selector: Selector<Omit<ContextValue, 'store'>, TSelected>
): TSelected {
	const context = useContext(AppContext);
	const outerValue = selector(context);

	return outerValue as TSelected;
}

export const useAppStore = getUseStoreHookSafe<AppWrapperStoreState, ContextValue>(AppContext);
